import * as React from 'react';

import { observer } from 'mobx-react';

import { Field, FormDropDown, FormStore } from '@estee/elc-form';
import { IFormValues } from '@estee/elc-form/typings/views/Form';

import { IDropdownOption } from '~domain/entities/Config';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { dropdownFieldClasses, fieldLabelClasses } from '~utils/Classes';
import { FieldValidations } from '~utils/FieldValidations';
import { useTranslation } from '~utils/hooks/translations';

import { DropDownViewController } from './DropDownViewController';

export interface IDropDownProps {
    inputLabel: string;
    selectLabel?: string;
    name: string;
    disabled: boolean;
    options?: IDropdownOption[];
    afterChange?(fieldName: string, value: string | number): void;
    fieldValidations: FieldValidations;
    formStore: FormStore<IFormValues>;
    withFloatingLabel?: boolean;
}

export const TEST_ID_DROP_DOWN_LABEL = 'iam-drop-down-label';
export const TEST_ID_DROP_DOWN_INPUT = 'iam-drop-down-input';
export const TEST_ID_DROP_DOWN_TRIGGER = 'iam-drop-down-trigger';

export const DropDown = observer((props: IDropDownProps) => {
    const {
        inputLabel,
        selectLabel,
        name,
        fieldValidations,
        disabled,
        options: propOptions,
        formStore,
        withFloatingLabel
    } = props;

    const { isRequired, validations, options: configOptions } = fieldValidations || {};
    const translations = useTranslation();
    const viewController = React.useMemo<DropDownViewController>(
        () => diContainer.get(serviceNames.dropDownViewController),
        []
    );

    React.useEffect(() => {
        viewController.loaded({
            name,
            selectLabel,
            formStore
        });

        return () => viewController.dispose();
    }, []);

    React.useEffect(() => {
        const fixedOptions =
            propOptions ??
            configOptions?.map(({ labelKey, label, value }) => ({
                label: labelKey ? translations[labelKey] : label,
                value
            }));
        fixedOptions && viewController.setFixedOptions(fixedOptions);
    }, [propOptions, configOptions]);

    const afterChange = React.useCallback(
        (fieldName: string, value: string | number) => {
            viewController.afterChange(fieldName, value);
            props.afterChange?.(fieldName, value);
        },
        [props.afterChange, viewController.afterChange]
    );

    return (
        <>
            {!withFloatingLabel && (
                <div
                    data-test-id={TEST_ID_DROP_DOWN_LABEL}
                    className={fieldLabelClasses(name, isRequired)}
                >
                    {inputLabel}
                </div>
            )}
            <Field
                name={name}
                withFloatingLabel={withFloatingLabel}
                inputLabel={inputLabel}
                customClassName={dropdownFieldClasses(name)}
                isRequired={isRequired}
                isDisabled={disabled}
                dataTestId={TEST_ID_DROP_DOWN_INPUT}
                dropdownDataTestId={TEST_ID_DROP_DOWN_TRIGGER}
                component={FormDropDown}
                options={viewController.data.options}
                afterChange={afterChange}
                validations={validations}
            />
        </>
    );
});
